// src/components/Navbar.jsx
import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaPhone, FaCalendarAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import "./Navbar.css";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  /* ────────── EFFECTS ────────── */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu whenever the route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /* ─────────── DATA ─────────── */
  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Pricing", path: "/pricing-details" },
    { name: "Article", path: "/articles" },
    { name: "Contact", path: "/contact" },
  ];

  /* ───────── HANDLERS ───────── */
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="navbar__container">
        {/* Logo */}
        <motion.div
          className="navbar__logo"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" aria-label="Greenworks Carpet Cleaning - Home">
            <img
              src={`${process.env.PUBLIC_URL}/green-logo.png`}
              alt="greenworkscarpetcleaning"
            />
          </Link>
        </motion.div>

        {/* Nav links */}
        <div className={`navbar__menu${menuOpen ? " active" : ""}`}>
          {navLinks.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              className={({ isActive }) =>
                `navbar__link${isActive ? " active" : ""}`
              }
              onClick={() => setMenuOpen(false)}
              end={link.path === "/"} // exact match for Home
            >
              <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                {link.name}
              </motion.span>
            </NavLink>
          ))}

          {/* Mobile buttons */}
          <div className="navbar__mobile-buttons">
            <motion.a
              href="tel:+12068996862"
              className="btn btn--outline"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMenuOpen(false)}
            >
              Call Us Now
            </motion.a>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/contact" className="btn btn--primary">
                Schedule Now
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Desktop buttons */}
        <div className="navbar__buttons">
          <motion.a
            href="tel:+12068996862"
            className="btn btn--outline"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaPhone className="btn__icon--left" />
            Call Us Now
          </motion.a>

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/contact" className="btn btn--primary">
              <FaCalendarAlt className="btn__icon--left" />
              Schedule Now
            </Link>
          </motion.div>
        </div>

        {/* Mobile menu toggle */}
        <motion.button
          className="navbar__toggle"
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          aria-controls="primary-navigation"
          whileTap={{ scale: 0.9 }}
        >
          {menuOpen ? (
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: 180 }}
              transition={{ duration: 0.3 }}
            >
              <FaTimes />
            </motion.div>
          ) : (
            <FaBars />
          )}
        </motion.button>
      </div>
    </nav>
  );
};

export default Navbar;
